import { useCallback } from 'react';
import { apiWithEntityId, SelltreezApi } from 'services/api';
import { useStateValue } from '../..';
import {
  ADD_LOCATION,
  DELETE_LOCATION,
  EDIT_LOCATION,
  FETCH_LOCATIONS,
  FETCH_DRAWERS,
  action,
  actionFailure,
  actionSuccess,
} from '../actions';
import { VIRTUAL_TERMINAL } from '../type';
import useOrganisation from 'state/organisation/hooks/useOrganisation';

const useVirtualTerminal = () => {
  const { organisation } = useOrganisation();
  const { activeOrg } = organisation;
  const { state, dispatch } = useStateValue();
  const { virtualTerminal } = state;

  const fetchLocationsList = useCallback(async () => {
    dispatch(action(FETCH_LOCATIONS));
    try {
      const response = await apiWithEntityId.get('/locations');
      dispatch(actionSuccess(FETCH_LOCATIONS, response?.data));
    } catch (e) {
      dispatch(actionFailure(FETCH_LOCATIONS, e));
    }
  }, [dispatch]);

  const deleteLocation = async (id: string) => {
    dispatch(action(DELETE_LOCATION));
    try {
      const response = await apiWithEntityId.remove(`/locations/${id}`);
      dispatch(actionSuccess(DELETE_LOCATION, response?.data));
    } catch (e) {
      dispatch(actionFailure(DELETE_LOCATION, e));
    }
  };

  const addLocation = async (request: VIRTUAL_TERMINAL.IAddLocationRequest) => {
    dispatch(action(ADD_LOCATION));
    try {
      const response = await apiWithEntityId.post(
        '/locations',
        request as unknown as Record<string, unknown>
      );
      dispatch(actionSuccess(ADD_LOCATION, response?.data));
    } catch (e) {
      dispatch(actionFailure(ADD_LOCATION, e));
    }
  };

  const editLocation = async (
    request: VIRTUAL_TERMINAL.IAddLocationRequest,
    id: string
  ) => {
    dispatch(action(EDIT_LOCATION));
    try {
      const response = await apiWithEntityId.patch(
        `/locations/${id}`,
        request as unknown as Record<string, unknown>
      );
      dispatch(actionSuccess(EDIT_LOCATION, response?.data));
    } catch (e) {
      dispatch(actionFailure(EDIT_LOCATION, e));
    }
  };

  const fetchDrawersList = useCallback(async () => {
    dispatch(action(FETCH_DRAWERS));
    try {
      // Create Selltreez API instance
      const selltreezApi = new SelltreezApi(activeOrg?.settings.sell_treez_url);

      // Restaurant ID is always 1 in SellTreez
      const drawersApiUrl = '/HintsService/v1.0/rest/account/restaurant/1/accounttype/CASH_DRAWER';
      // Add pagination to limit response size (most restaurants have <10 open shifts)
      const shiftsApiUrl = '/HintsService/v1.0/rest/shift/restaurant/1/getopenshifts';

      // Fetch drawers first
      const drawersResponse = await selltreezApi.get<{
        resultCode: string;
        resultReason: string | null;
        data: VIRTUAL_TERMINAL.IDrawer[];
      }>(drawersApiUrl);

      const drawersData = drawersResponse.data?.data || [];

      // Filter to only show enabled cash drawers
      const enabledDrawers = drawersData.filter(drawer => drawer.currentFlag === true);

      // Early return if no enabled drawers - skip unnecessary shift API call
      if (enabledDrawers.length === 0) {
        dispatch(actionSuccess(FETCH_DRAWERS, {
          ...drawersResponse.data,
          data: enabledDrawers,
        }));
        return;
      }

      // Only fetch shifts if we have drawers
      const shiftsResponse = await selltreezApi.get<{
        resultCode: string;
        resultReason: string | null;
        data: Array<{
          cashDrawerId: string;
          cashDrawerName: string;
          dateClose: number | null;
        }>;
      }>(shiftsApiUrl, { entries: 50, page: 0 }).catch(() => ({ data: { data: [] } })); // Fallback if shifts endpoint fails

      // Filter to get only truly OPEN shifts (dateClose is null)
      const openShifts = (shiftsResponse.data?.data || []).filter(
        shift => shift.dateClose === null
      );

      // Create a set of drawer IDs that have open shifts
      const activeDrawerIds = new Set(
        openShifts.map(shift => shift.cashDrawerId)
      );

      // Mark drawers as active if they have an open shift
      const drawersWithActiveFlag = enabledDrawers.map(drawer => {
        const isActive = activeDrawerIds.has(drawer.id);
        return isActive ? { ...drawer, isActive } : drawer;
      });

      dispatch(actionSuccess(FETCH_DRAWERS, {
        ...drawersResponse.data,
        data: drawersWithActiveFlag,
      }));
    } catch (e) {
      dispatch(actionFailure(FETCH_DRAWERS, e));
    }
  }, [dispatch, activeOrg]);

  return {
    virtualTerminal,
    fetchLocationsList,
    deleteLocation,
    addLocation,
    editLocation,
    fetchDrawersList,
  };
};

export default useVirtualTerminal;
